<template>
  <ValidationObserver v-slot="{ handleSubmit }">
    <b-modal
      id="addEditEndpointModal"
      v-model="modal.show"
      :title="isEdit ? `Edit Endpoint` : `Add New Endpoint`"
      :ok-disabled="isDisable || disableByFieldValue"
      :ok-title="`Submit`"
      @ok.prevent="handleSubmit(onSubmit)"
      @hidden="onHideModal"
      @show="onShowModal"
    >
      <content-spinner :show="modal.loading" />
      <show-error
        :show="showError"
        :text="modalError"
      />
      <b-form
        v-if="!modal.loading"
        @submit.prevent="handleSubmit(onSubmit)"
        @keydown.enter.prevent="handleSubmit(onSubmit)"
      >
        <input-widget
          v-for="field in fields"
          :id="`addEditEndpoint-${field.name}-field`"
          :model="form"
          :field="field"
          :autofocus="field.autofocus"
          :disabled="isEdit && field.name === 'entityName'"
          @multi-select-open="onMultiSelectOpen"
        />
      </b-form>
      <div
        slot="modal-footer"
        class="d-flex"
      >
        <b-button
          id="addEditEndpointModalCancelBtn"
          class="mt-3 mr-3"
          block
          @click="$bvModal.hide('addEditEndpointModal')"
        >
          Cancel
        </b-button>
        <b-button
          id="addEditEndpointModalOkBtn"
          class="mt-3"
          variant="primary"
          block
          @click.prevent="handleSubmit(onSubmit);"
        >
          Submit
        </b-button>
      </div>
    </b-modal>
    <DataManagementModal
      :is-starting-point="true"
      :selected-field="{}"
      @on-submit="onChooseEntityField"
    />
  </ValidationObserver>
</template>

<script>
import {createNamespacedHelpers} from 'vuex';
import ContentSpinner from '../../../core/components/ContentSpinner';
import InputWidget from '../../../core/components/complex-table/components/InputWidget';
import {v4 as uuidv4} from 'uuid';
import ShowError from '../../../core/components/ShowError';
import DataManagementModal from '../../../core/components/complex-table/DataManagementModal';

const {mapState, mapActions} = createNamespacedHelpers('connectorScreen');

const {
  mapState: mapDataManagementState,
  mapActions: mapDataManagementActions,
} = createNamespacedHelpers('dataManagement');

export default {
  name: 'EndpointModal',
  components: {ShowError, InputWidget, ContentSpinner, DataManagementModal},
  data() {
    return {
      showError: false,
      isDisable: false,
      modalError: '',
      form: {
        baseNode: '',
        url: '',
        friendlyName: '',
        entityPrimaryKey: null,
        entityName: '',
        methodType: null,
        name: '',
        endpointConfigurationJSON: '',
        dbName: null,
        warehouseName: null,
        customSQL: null,
        tableName: null,
        schemaName: null,
        businessKeys: [],
        lookupFilterColumns: [],
        whereClause: null,
        id: '',
      },
      defaultFields: {
        friendlyName: {
          type: 'text',
          required: true,
          autofocus: true,
          label: 'Friendly Name',
          name: 'friendlyName',
        },
        entityName: {
          type: 'text',
          required: true,
          label: 'Entity Name',
          name: 'entityName',
        },
        entityPrimaryKey: {
          type: 'text',
          label: 'Entity Primary Key',
          name: 'entityPrimaryKey',
        },
        baseNode: {
          type: 'text',
          label: 'Base Node',
          name: 'baseNode',
        },
        url: {
          type: 'text',
          label: 'URL',
          name: 'url',
        },
        methodType: {
          type: 'select',
          required: true,
          label: 'Method Type',
          name: 'methodType',
          data: {
            options: [
              {value: null, text: 'Select Method Type', disabled: true},
              {value: 'GET', text: 'GET'},
              {value: 'POST', text: 'POST'},
              {value: 'PUT', text: 'PUT'},
            ],
          },
        },
        endpointConfigurationJSON: {
          type: 'textarea',
          required: false,
          label: 'Configuration JSON',
          name: 'endpointConfigurationJSON',
        },
      },
      snowflakeEndpointConfigurationJsonFields: {
        dbName: {
          type: 'text',
          label: 'DB Name',
          name: 'dbName',
        },
        warehouseName: {
          type: 'text',
          label: 'Warehouse Name',
          name: 'warehouseName',
        },
        tableName: {
          type: 'text',
          label: 'Table Name',
          name: 'tableName',
        },
        schemaName: {
          type: 'text',
          label: 'Schema Name',
          name: 'schemaName',
        },
        businessKeys: {
          type: 'tags',
          label: 'Business Keys',
          name: 'businessKeys',
        },
        lookupFilterColumns: {
          type: 'multi-select',
          required: false,
          label: 'Lookup Filter Columns',
          name: 'lookupFilterColumns',
          isFilterColumns: true,
          config: {
            optionValue: 'value',
            optionLabel: 'text',
            placeholder: 'Select field',
            multiple: true,
            taggable: true,
          },
          data: {
            options: [],
          },
        },
        customSQL: {
          type: 'textarea',
          label: 'Custom SQL',
          name: 'customSQL',
        },
        whereClause: {
          type: 'textarea',
          label: 'Where Clause',
          name: 'whereClause',
        },
      },
    };
  },
  computed: {
    ...mapState({
      modal: state => state.endpoint.modal,
    }),
    ...mapDataManagementState({
      tableFields: state => state.fields.data,
    }),
    fields() {
      let fields = {};
      Object.keys(this.defaultFields).forEach(fieldName => {
        if (fieldName === 'endpointConfigurationJSON' && !this.isBlackMountain) return false;
        fields[fieldName] = this.defaultFields[fieldName];
      });

      if (this.isSnowflake) {
        fields = this.snowflakeEndpointConfigurationJsonFields;
      }
      return fields;
    },
    isBlackMountain() {
      return this.modal.data.vendor === 'BlackMountain';
    },
    isEdit() {
      return this.modal.data && Object.keys(this.modal.data).length > 2;
    },
    isSnowflake() {
      return this.modal.data.vendor === 'Snowflake';
    },
    disableByFieldValue() {
      return this.isSnowflake && !this.form.lookupFilterColumns.length && !this.form.whereClause;
    },
  },
  methods: {
    ...mapActions(['hideEndpointModal', 'saveEndpoint']),
    ...mapDataManagementActions(['showDataManagementModal']),
    async onSubmit() {
      this.isDisable = true;
      const params = {...this.form};
      let action = 'update';

      if (!params.configurationId) {
        params.configurationId = this.modal.data.configurationId;
      }
      if (!params.endpointId) {
        params.endpointId = uuidv4();
        action = 'create';
      }

      if (this.isSnowflake) {
        params.methodType = 'GET';
        params.EndpointConfigurationJSON = JSON.stringify({
          dbName: this.form.dbName,
          warehouseName: this.form.warehouseName,
          tableName: this.form.tableName,
          schemaName: this.form.schemaName,
          businessKeys: this.form.businessKeys,
          lookupFilterColumns: this.form.lookupFilterColumns.map(o => o.value),
          customSQL: this.form.customSQL,
          whereClause: this.form.whereClause,
        });
      }

      const {success, body} = await this.saveEndpoint({data: params, action});
      if (success) {
        this.$toasted.success(`Endpoint ${action}d successfully`, {
          duration: TOASTER_DURATION,
        });
        this.onHideModal();
      } else {
        this.isDisable = false;
        this.showError = true;
        this.modalError = body.ErrorMessage;
      }
    },
    onHideModal() {
      this.form = {
        baseNode: '',
        url: '',
        friendlyName: '',
        entityPrimaryKey: null,
        entityName: '',
        methodType: null,
        name: '',
        endpointConfigurationJSON: '',
        dbName: null,
        warehouseName: null,
        tableName: null,
        schemaName: null,
        businessKeys: [],
        lookupFilterColumns: [],
        customSQL: null,
        whereClause: null,
        id: '',
      };
      this.isDisable = false;
      this.showError = false;
      this.modalError = '';
      this.hideEndpointModal();
    },
    onShowModal() {
      if (this.isEdit) {
        this.form = {...this.modal.data};

        if (this.isSnowflake) {
          const endpointConfigurationJSON = JSON.parse(this.modal.data.endpointConfigurationJSON);

          this.form = {
            ...this.form,
            ...endpointConfigurationJSON,
            lookupFilterColumns: endpointConfigurationJSON.lookupFilterColumns.map(fieldGuid => {
              const field = this.tableFields.find(f => f.fieldGuid === fieldGuid);

              if (field) {
                return {
                  value: fieldGuid,
                  text: field.displayName,
                };
              }

              return {};
            }),
          };

        }
      }
    },
    onMultiSelectOpen() {
      this.showDataManagementModal();
      document.activeElement.blur();
    },
    onChooseEntityField(field) {
      this.form.lookupFilterColumns.push({
        value: field.fieldGuid,
        text: field.displayName,
      });

      this.snowflakeEndpointConfigurationJsonFields.lookupFilterColumns.data.options.push({
        value: field.fieldGuid,
        text: field.displayName,
      });
    },
  },
};
</script>

<style scoped>

</style>
